import cx from 'classnames';
// biome-ignore lint/correctness/noUnusedImports: existing error
import React, {useCallback} from 'react';
import {useSelector} from 'react-redux';
import {v4} from 'uuid';
import {eventIsPremium} from '~/CreateFabricGreetingCard/reducers/details/selectors';
import {useCurrentRoute} from '~/CreateFabricInvitation/navigation';
import {selectUsedPremiumFeature} from '~/CreateFabricInvitation/reducers/fabricDesign/selectors';
import {selectIsPremium} from '~/CreateFabricInvitation/reducers/fabricTemplate/selectors';
import {BottomButton} from '~/CreateFabricInvitation/routes/InvitationMaker/OneStepCreatePage/components/BottomButton/BottomButton';
import {Options} from '~/CreateFabricInvitation/routes/InvitationMaker/OneStepCreatePage/components/Options/Options';
import {useBackground} from '~/common/FabricInvitation/EventCreateDesign/components/Background/actions/useBackground';
import {useEventValidation} from '~/common/FabricInvitation/EventCreateDesign/hooks/useEventValidation';
import {GoogleAdBanner} from '~/common/fabric/GoogleAds/GoogleAdBanner';
import {getEventId} from '~/common/fabric/reducers/eviteEvent/selectors';
import {UPSELL_MODES, UpsellModal} from '~/common/fabric/UpsellModal/UpsellModal';
import {isFabricWebview} from '~/common/utils/webview';
import {usePremiumFeatureBlocker} from '~/OneStepCreateInvitation/hooks/usePremiumFeatureBlocker';
import {DetailsFields} from '~/OneStepCreateInvitation/routes/OneStepCreatePage/components/DetailsFields/DetailsFields';
import {useUpsellModal} from '~/OneStepCreateInvitation/routes/OneStepCreatePage/components/UpsellModal/useUpsellModal';
import {useGetAdsQuery} from '~/ViewInvitation/api/endpoints/content';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';

import './OneStepCreatePage.sass';

const FIRST_AFFILIATE_AD_ID = v4();
const SECOND_AFFILIATE_AD_ID = v4();

export const OneStepCreatePage = () => {
  const {isUsingPremiumFeature, shouldShowModal, dismissModal, confirmUpgrade} =
    usePremiumFeatureBlocker();
  const isEventPremium = useSelector(eventIsPremium);
  const isPremium = useSelector(selectIsPremium);
  const usedPremiumFeature = useSelector(selectUsedPremiumFeature);
  const eventId = useSelector(getEventId);
  const isLargishAndUp = useIsLargishAndUp();
  const {onTextureChange, selectedBackground} = useBackground(!isLargishAndUp);
  const {nextButtonText, previousPath} = useCurrentRoute();
  const isBottomCtaShowing = usedPremiumFeature && !isPremium;

  const {toggleUpsellModal, UpsellModalComponent} = useUpsellModal('remove-ads');

  const confirmUpsell = useCallback(() => {
    confirmUpgrade();
    if (isUsingPremiumFeature) {
      onTextureChange(selectedBackground?.icon);
    }
  }, [confirmUpgrade, isUsingPremiumFeature, selectedBackground, onTextureChange]);

  const {firstAffiliateAd, secondAffiliateAd} = useGetAdsQuery(eventId, {
    skip: isEventPremium || isFabricWebview(),
    selectFromResult: ({data}) => ({
      firstAffiliateAd: data?.ad_before?.host,
      secondAffiliateAd: data?.ad_after?.host,
    }),
  });

  useEventValidation();

  return (
    <div
      className={cx({
        'one_step_flow-event-content': true,
        'grayed-out': isUsingPremiumFeature,
      })}
    >
      <DetailsFields isEventPremium={isEventPremium} />

      {!isEventPremium && firstAffiliateAd && (
        <>
          <GoogleAdBanner
            index={FIRST_AFFILIATE_AD_ID}
            isCreateEditPreview
            removeOnClick={() => toggleUpsellModal('remove-ads')}
            showRemoveButton
            pageType="createflow"
          />
          <UpsellModalComponent />
        </>
      )}

      <Options />

      {!isEventPremium && secondAffiliateAd && (
        <GoogleAdBanner
          index={SECOND_AFFILIATE_AD_ID}
          isCreateEditPreview
          removeOnClick={() => toggleUpsellModal('remove-ads')}
          showRemoveButton
          pageType="createflow"
        />
      )}

      {!isBottomCtaShowing && (
        <BottomButton hasCtaBeenSeen text={nextButtonText} previousRoute={previousPath} />
      )}
      {shouldShowModal ? (
        <UpsellModal
          noAutoRestore
          toggleUpsellModal={dismissModal}
          closeOverlay={dismissModal}
          onConfirmAction={confirmUpsell}
          mode={UPSELL_MODES.UNLOCK_DESIGN_TOOLS}
          cancelCtaText="Dismiss"
          hideSubtitle
        />
      ) : null}
    </div>
  );
};
